import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { problemOf } from '../api/client'
import { useAction, useActionMutations, useActions, useCompanies, type ActionFilters, type ActionListItem, type ActionStatus } from '../api/hooks'
import { useAuth } from '../auth/AuthProvider'
import { Badge, Empty, Modal, SecureImage, fmtDate, fmtDateTime, severityColor } from '../components/ui'
import DateInput from '../components/DateInput'

const statusColor: Record<ActionStatus, string> = { Open: '#2a78d6', InProgress: '#eda100', ClosureRequested: '#4a3aa7', Closed: '#008300', Overdue: '#e34948', Rejected: '#eb6834' }

export default function ActionsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const set = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); if (k !== 'page') n.delete('page'); setParams(n) }
  const filters: ActionFilters = {
    page: Number(params.get('page') ?? 1), pageSize: 25, sort: params.get('sort') ?? 'dueDate', search: params.get('search') ?? '',
    status: (params.get('status') as ActionStatus | null) ?? '', companyId: params.get('companyId') ?? '',
    mineOnly: params.get('mine') === '1', overdueOnly: params.get('overdue') === '1', openOnly: params.get('open') !== '0',
  }
  const actions = useActions(filters)
  const companies = useCompanies()
  const selected = params.get('id')
  const isSupplier = user?.roles.every((r) => r.role === 'Supplier')

  return (
    <div className="stack">
      <h2>{t('actions.title')}</h2>
      <div className="card filters">
        <input placeholder={t('common.search')} value={filters.search} onChange={(e) => set('search', e.target.value)} />
        <select value={filters.status} onChange={(e) => set('status', e.target.value)}>
          <option value="">{t('actions.allStatuses')}</option>
          {(Object.keys(statusColor) as ActionStatus[]).map((s) => <option key={s} value={s}>{t(`actionStatus.${s}`)}</option>)}
        </select>
        {!isSupplier ? (
          <select value={filters.companyId} onChange={(e) => set('companyId', e.target.value)}>
            <option value="">{t('common.allCompanies')}</option>
            {companies.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ) : null}
        <label className="row"><input type="checkbox" checked={filters.mineOnly} onChange={(e) => set('mine', e.target.checked ? '1' : '')} /> {t('actions.mine')}</label>
        <label className="row"><input type="checkbox" checked={filters.overdueOnly} onChange={(e) => set('overdue', e.target.checked ? '1' : '')} /> {t('actions.overdue')}</label>
        <label className="row"><input type="checkbox" checked={filters.openOnly} onChange={(e) => set('open', e.target.checked ? '' : '0')} /> {t('actions.openOnly')}</label>
      </div>

      <div className="card">
        {actions.isLoading ? <p>{t('common.loading')}</p> : !actions.data?.items.length ? <Empty text={t('actions.empty')} /> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>{t('finding.actionNo')}</th><th>{t('actions.status')}</th><th>{t('finding.action')}</th><th>{t('finding.responsible')}</th><th>{t('finding.dueDate')}</th><th>{t('companies.title')}</th><th>{t('reports.code')}</th><th>{t('finding.severity')}</th></tr></thead>
              <tbody>
                {actions.data.items.map((a) => (
                  <tr key={a.id} onClick={() => set('id', a.id)} style={{ cursor: 'pointer' }}>
                    <td><b>{a.actionNo}</b></td>
                    <td><Badge color={statusColor[a.status]}>{t(`actionStatus.${a.status}`)}</Badge></td>
                    <td className="small">{a.actionText.length > 140 ? a.actionText.slice(0, 140) + '…' : a.actionText}<div className="muted small">#{a.findingNumber} · {a.findingDescription.slice(0, 80)}</div></td>
                    <td className="small">{a.responsibleName}</td>
                    <td>{fmtDate(a.dueDate)}{a.daysLeft != null && a.status !== 'Closed' ? <div className={`small ${a.isOverdue ? 'error' : 'muted'}`}>{a.isOverdue ? t('actions.overdueBy', { days: -a.daysLeft }) : t('actions.daysLeft', { days: a.daysLeft })}</div> : null}</td>
                    <td className="small">{a.companyName}</td>
                    <td><Link to={`/reports/${a.formInstanceId}`} onClick={(e) => e.stopPropagation()}>{a.reportCode ?? a.templateCode}</Link></td>
                    <td><Badge color={severityColor[a.severity]}>{t(`severity.${a.severity}`)}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {actions.data && actions.data.totalPages > 1 ? (
          <div className="row between" style={{ marginTop: 12 }}>
            <span className="muted small">{t('common.pageOf', { page: actions.data.page, pages: actions.data.totalPages, total: actions.data.totalCount })}</span>
            <div className="row"><button className="secondary" disabled={actions.data.page <= 1} onClick={() => set('page', String(actions.data!.page - 1))}>‹</button><button className="secondary" disabled={actions.data.page >= actions.data.totalPages} onClick={() => set('page', String(actions.data!.page + 1))}>›</button></div>
          </div>
        ) : null}
      </div>

      {selected ? <ActionModal id={selected} onClose={() => set('id', '')} /> : null}
    </div>
  )
}

function ActionModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const detail = useAction(id)
  const m = useActionMutations(id)
  const [note, setNote] = useState('')
  const [dueDate, setDueDate] = useState('')
  const canVerify = user?.roles.some((r) => ['SuperAdmin', 'TenantAdmin', 'Auditor'].includes(r.role))
  const a: ActionListItem | undefined = detail.data?.summary
  const d = detail.data
  if (!a || !d) return <Modal title="…" onClose={onClose}><p>{t('common.loading')}</p></Modal>
  const err = [m.start, m.requestClosure, m.verify, m.reassign, m.uploadEvidence].find((x) => x.isError)

  return (
    <Modal title={`${t('finding.actionNo')} ${a.actionNo}`} onClose={onClose} width={760}>
      <div className="stack">
        <div className="row wrap">
          <Badge color={statusColor[a.status]}>{t(`actionStatus.${a.status}`)}</Badge>
          <Badge color={severityColor[a.severity]}>{t(`severity.${a.severity}`)}</Badge>
          <span className="muted small">{a.companyName} · <Link to={`/reports/${a.formInstanceId}`}>{a.reportCode ?? a.templateCode}</Link>{a.questionNo ? ` · S.${a.questionNo}` : ''}</span>
        </div>
        {d.questionText ? <p className="small"><b>{t('finding.question')}:</b> {d.questionText}</p> : null}
        <p className="small"><b>{t('finding.description')}:</b> {a.findingDescription}</p>
        <p style={{ whiteSpace: 'pre-wrap' }}><b>{t('finding.action')}:</b> {a.actionText}</p>
        <div className="grid two">
          <div><span className="muted small">{t('finding.responsible')}</span><div>{a.responsibleName}</div></div>
          <div><span className="muted small">{t('finding.dueDate')}</span><div className={a.isOverdue ? 'error' : ''}>{fmtDate(a.dueDate)}</div></div>
        </div>
        {d.findingPhotos.length > 0 ? <div><span className="muted small">{t('actions.findingPhotos')}</span><div className="thumbs">{d.findingPhotos.map((p) => <SecureImage key={p.id} fileId={p.thumbnailFileId ?? p.fileId} alt="" className="thumb" />)}</div></div> : null}
        <div>
          <span className="muted small">{t('actions.evidence')} ({d.evidencePhotos.length})</span>
          <div className="thumbs">{d.evidencePhotos.map((p) => <SecureImage key={p.id} fileId={p.thumbnailFileId ?? p.fileId} alt="" className="thumb" />)}</div>
          {a.status !== 'Closed' ? <label className="button secondary small-btn" style={{ marginTop: 6 }}>{t('actions.uploadEvidence')}<input type="file" accept="image/*" multiple hidden onChange={(e) => { for (const f of e.target.files ?? []) m.uploadEvidence.mutate({ formInstanceId: a.formInstanceId, file: f }) }} /></label> : null}
        </div>
        {d.closureNote ? <p className="small"><b>{t('actions.closureNote')}:</b> {d.closureNote} <span className="muted">({d.closureRequestedByName}, {fmtDateTime(a.closureRequestedAt)})</span></p> : null}
        {d.verificationNote ? <p className="small"><b>{t('actions.verificationNote')}:</b> {d.verificationNote} <span className="muted">({d.verifiedByName})</span></p> : null}

        {a.status !== 'Closed' ? (
          <div className="card stack" style={{ background: 'var(--hover)' }}>
            {a.status !== 'ClosureRequested' ? (
              <>
                <textarea rows={2} placeholder={t('actions.closureNotePlaceholder')} value={note} onChange={(e) => setNote(e.target.value)} />
                <div className="row wrap">
                  {a.status === 'Open' || a.status === 'Overdue' || a.status === 'Rejected' ? <button className="secondary" onClick={() => m.start.mutate()}>{t('actions.start')}</button> : null}
                  <button onClick={() => m.requestClosure.mutate(note)} disabled={!note.trim() || d.evidencePhotos.length === 0}>{t('actions.requestClosure')}</button>
                  {d.evidencePhotos.length === 0 ? <span className="muted small">{t('actions.evidenceRequired')}</span> : null}
                </div>
              </>
            ) : null}
            {canVerify ? (
              <div className="row wrap">
                <input placeholder={t('actions.verifyNote')} value={note} onChange={(e) => setNote(e.target.value)} style={{ flex: 1 }} />
                <button onClick={() => m.verify.mutate({ approve: true, note })} disabled={a.status !== 'ClosureRequested' && !user?.roles.some((r) => r.role === 'TenantAdmin')}>{t('actions.approve')}</button>
                <button className="danger" onClick={() => m.verify.mutate({ approve: false, note })} disabled={a.status !== 'ClosureRequested' || !note.trim()}>{t('actions.reject')}</button>
              </div>
            ) : null}
            {canVerify ? (
              <div className="row wrap">
                <DateInput value={dueDate} onChange={setDueDate} style={{ width: 'auto' }} />
                <button className="secondary" onClick={() => m.reassign.mutate({ dueDate })} disabled={!dueDate}>{t('actions.changeDueDate')}</button>
              </div>
            ) : null}
            {err ? <p className="error small">{problemOf(err.error).title}</p> : null}
          </div>
        ) : null}

        <details><summary className="muted small">{t('actions.history')} ({d.history.length})</summary>
          <ul className="small">{d.history.map((h, i) => <li key={i}>{fmtDateTime(h.at)} · {h.userEmail} · <code>{h.change.slice(0, 160)}</code></li>)}</ul>
        </details>
      </div>
    </Modal>
  )
}
